import React, { FocusEvent, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { DateTime } from 'luxon';

import {
	TypographyComponent,
	TypographySize
} from '@vaa-component-lib/component.atom.typography';
import { TextFieldComponent } from '@vaa-component-lib/component.atom.text-field';
import {
	IconActionCalendarComponent,
	IconFeedbackWarningComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';

import PopOverComponent, {
	PopOverAlignment
} from '../pop-over/pop-over.component';
import CriteriaTriggerComponent from '../criteria-trigger/criteria-trigger.component';
import DatePickerComponent from '../date-picker/date-picker.component';

import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import {
	DATE_FORMATS,
	DATEPICKER_ERROR_MESSAGE
} from '../../lib/utils/constants';
import sanitiseString from '../../lib/utils/sanitise-string';
import { PRODUCT_TYPES } from '../../types/criteria';

import {
	Alert,
	AlertMessage,
	DatePickerField,
	FooterContent
} from './date-picker-field.styles';

export interface DatePickerFieldComponentProps {
	label: string | undefined;
	name: string;
	placeholder?: string | undefined;
	id: string | undefined;
	value: string[] | undefined;
	hasError?: boolean;
	feedback?: string;
	bookingType?: string;
	enabledDateRange?: string[];
	selectionType?: 'range' | 'single';
	onChange?: (value: string[]) => void;
}

const DatePickerFieldComponent = ({
	label,
	name,
	placeholder,
	id,
	value,
	hasError,
	feedback,
	bookingType,
	enabledDateRange,
	selectionType = 'range',
	onChange
}: DatePickerFieldComponentProps) => {
	const { setValue, getFieldState } = useFormContext();
	const [showPopOver, setShowPopOver] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);
	const [nextButtonIsActive, setNextButtonIsActive] = useState<boolean>(true);
	const isMobile = useMediaQuery(`(max-width: ${BreakPointToken.SmMax}px)`);
	let durationHotel;

	useEffect(() => {
		const areValuesSetOnLoad = value && value.length > 0;
		setNextButtonIsActive(areValuesSetOnLoad);
	}, []);

	// Popover handling
	const handleClickOutside = (event: MouseEvent) => {
		if (ref.current && !ref.current.contains(event.target as Node)) {
			setShowPopOver(false);
		}
	};
	const handleFocus = () => {
		setShowPopOver(true);
	};

	const toggleFocus = () => {
		setShowPopOver(!showPopOver);
	};

	const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
		if (
			e.relatedTarget &&
			ref.current &&
			!ref.current.contains(e.relatedTarget)
		) {
			setShowPopOver(false);
		}
	};

	const isDurationError =
		getFieldState(name)?.error &&
		getFieldState(name)?.error.message === DATEPICKER_ERROR_MESSAGE.duration;
	const isDurationOver28Days = (val: string[]) =>
		DateTime.fromISO(val[1]).diff(DateTime.fromISO(val[0]), 'days').days > 28;

	const isFlightOnly = bookingType === PRODUCT_TYPES.FLIGHT_ONLY;
	const isHoliday = [
		PRODUCT_TYPES.HOLIDAY,
		PRODUCT_TYPES.FLIGHT_HOTEL,
		PRODUCT_TYPES.HOTEL,
		PRODUCT_TYPES.FLIGHT_CAR,
		PRODUCT_TYPES.MULTICENTRE,
		PRODUCT_TYPES.FLYDRIVE
	].includes(bookingType as PRODUCT_TYPES);

	useEffect(() => {
		if (document) {
			if (showPopOver) {
				document.addEventListener('mousedown', handleClickOutside);
			}

			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [showPopOver]);

	const popOverId = `popover-${sanitiseString(label || '')}`;

	// Displays dates with special format on input field
	const displayValueDates = value
		.map((val: string) => {
			return DateTime.fromISO(val).toFormat(DATE_FORMATS.FIELD_DISPLAY_DATE);
		})
		.join(' - ');

	const displayValueYear = DateTime.fromISO(value[1] || value[0]).toFormat(
		'yyyy'
	);

	const displayValue =
		value.length > 0 ? `${displayValueDates}, ${displayValueYear}` : '';

	// Calculates the duration and display correct format
	if (value.length && isHoliday) {
		const startDate = DateTime.fromISO(value[0]);
		const endDate = value.length === 2 ? DateTime.fromISO(value[1]) : startDate;

		if (startDate.isValid && endDate.isValid) {
			const durationInDays = endDate.diff(startDate, 'days').days;
			if (isMobile) {
				durationHotel = `(${durationInDays} ${durationInDays > 1 ? 'nights' : 'night'})`;
			} else {
				durationHotel = `${durationInDays} ${durationInDays > 1 ? 'nights trip' : 'night trip'}`;
			}
		}
	}

	// Shows different content for popover depending on booking type and viewport
	const popOverFooter =
		(isFlightOnly && isMobile && displayValue && (
			<TypographyComponent
				element={'p'}
				size={isMobile ? TypographySize.Sml : TypographySize.Mdm}>
				{displayValue}
			</TypographyComponent>
		)) ||
		(isHoliday && displayValue && (
			<FooterContent data-cy="flight-hotel-popover-footer">
				<TypographyComponent
					element={'p'}
					size={isMobile ? TypographySize.Sml : TypographySize.Mdm}>
					{isMobile && displayValue} {durationHotel}
				</TypographyComponent>
				{isDurationError && (
					<Alert data-testid="passenger-error">
						<IconFeedbackWarningComponent
							size={isMobile ? IconsSize.Sml : IconsSize.Med}
						/>
						<AlertMessage>{getFieldState(name)?.error?.message}</AlertMessage>
					</Alert>
				)}
			</FooterContent>
		));

	return (
		<DatePickerField ref={ref} data-cy="date-picker-field">
			{isMobile && (
				<CriteriaTriggerComponent
					label={label}
					value={displayValue}
					iconChild={<IconActionCalendarComponent size={IconsSize.Sml} />}
					hasError={hasError}
					errorMessage={feedback}
					onClick={() => setShowPopOver(true)}
					aria={{ controls: popOverId, expanded: showPopOver }}
				/>
			)}
			{!isMobile && (
				<TextFieldComponent
					{...{
						label,
						placeholder,
						id,
						value
					}}
					onFocus={handleFocus}
					label={label}
					placeholder={''}
					value={displayValue}
					onBlur={handleBlur}
					autoComplete="off"
					spellCheck={false}
					iconChild={
						<IconActionCalendarComponent
							aria-label="Select dates"
							// @ts-ignore
							role="img"
						/>
					}
					onIconClick={toggleFocus}
					aria-controls={popOverId}
					aria-haspopup="true"
					readonly
					feedback={!showPopOver && feedback}
					hasError={(!showPopOver || isDurationError) && hasError}
				/>
			)}

			<PopOverComponent
				id={popOverId}
				isVisible={showPopOver}
				title={label}
				maxHeight={true}
				footerContent={popOverFooter}
				footerBtnText={'Next'}
				hasFooterBtn={isMobile || bookingType === PRODUCT_TYPES.FLIGHT_ONLY}
				footerOnClick={() => setShowPopOver(false)}
				isFooterBtnDisabled={!nextButtonIsActive || isDurationError}
				onClose={() => setShowPopOver(false)}
				alignment={PopOverAlignment.Center}
				isOverflow={true}>
				<DatePickerComponent
					bookingType={bookingType}
					{...(isHoliday && { enabledDateRange: enabledDateRange })}
					values={value}
					selectionType={selectionType}
					onChange={(value) => {
						setNextButtonIsActive(false);
						setValue(name, value, { shouldValidate: true });
						onChange?.(value);
					}}
					onSelectionComplete={(value) => {
						isMobile ? setNextButtonIsActive(true) : setShowPopOver(false);
						isHoliday && isDurationOver28Days(value) && setShowPopOver(true);
					}}
					isVisible={showPopOver}
				/>
			</PopOverComponent>
		</DatePickerField>
	);
};

export default DatePickerFieldComponent;
